import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { app_url } from "../../../config";
import toast from "react-hot-toast";
import ImageUploadModal from "../../../components/modals/ImageUploadModal";
import Error500 from "../../../components/500/Error500";

const modules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ direction: "rtl" }],
      [{ align: [] }],
      ["link", "image", "video"],
      ["clean"],
      ["blockquote", "code-block"],
      [{ color: [] }, { background: [] }],
    ],
  },
  clipboard: {
    matchVisual: false,
  },
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
  "code-block",
  "color",
  "background",
  "align",
  "script",
  "alt",
  "style",
];

const CreateBlog = () => {
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("adminEtijwaal"))?.token;
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const quillRef = useRef(null);
  const [networkIssue, setNetworkIssue] = useState(false);
  const [failed, setFailed] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    url_name: "",
    short_detail: "",
    meta_web_title: "",
    meta_web_description: "",
    meta_og_title: "",
    meta_og_description: "",
    title: "",
    description: "",
    is_active: 1,
  });

  // Custom image handler for React Quill - now opens modal
  const imageHandler = React.useCallback(() => {
    setShowImageModal(true);
  }, []);

  // Create memoized modules with handler
  const quillModules = React.useMemo(
    () => ({
      ...modules,
      toolbar: {
        ...modules.toolbar,
        handlers: {
          ...modules.toolbar.handlers,
          image: imageHandler,
        },
      },
    }),
    [imageHandler],
  );

  const handleError = (err) => {
    if (err.response) {
      if (err.response.status === 400) {
        toast.error(err.response.data.message || "Bad Request");
      } else if (err.response.status === 401) {
        navigate("/");
      } else if (err.response.status === 500) {
        toast.error(err.response.data.message || "Internal Server Error");
      } else {
        toast.error("An unexpected error occurred.");
      }
    } else {
      setNetworkIssue(true);
      toast.error("Network error. Please check your internet connection.");
      setFailed("Network error. Please check your internet connection.");
    }
  };

  // Handle image upload from modal
  const handleImageUploadFromModal = async (imageData, altText) => {
    // console.log("Image upload called with:", imageData);

    if (imageData.size > 1 * 1024 * 1024) {
      toast.error("Image size should be less than 1MB");
      return;
    }

    let uploadedImageUrl = null;

    // If it's a File object, upload to API
    if (imageData instanceof File) {
      try {
        // Create FormData for image upload
        const imageFormData = new FormData();
        imageFormData.append("blog_image", imageData);

        // Upload image to API
        const response = await axios.post(
          `${app_url}/api/6523-56545-4552/uploadContentImage`,
          imageFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );

        if (response.data.success && response.data.url) {
          uploadedImageUrl = response.data.url;
          toast.success("Image uploaded successfully!");
        } else {
          toast.error(response.data.message);
          return;
        }
      } catch (error) {
        handleError(error);
      }
    } else {
      // If it's already a URL
      uploadedImageUrl = imageData;
    }

    // Insert image into editor content
    if (uploadedImageUrl) {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      // IMPORTANT: Save the current selection BEFORE any async operations
      // Get current cursor position
      const range = editor.getSelection();
      const cursorPosition = range ? range.index : editor.getLength() - 1;

      // console.log("Inserting at position:", cursorPosition);
      // console.log("Editor length:", editor.getLength());

      // Create the image HTML
      const imgHtml = `<img src="${uploadedImageUrl}" alt="${
        altText || ""
      }" style="max-width: 100%;" />`;

      // Insert the HTML at the saved cursor position
      editor.clipboard.dangerouslyPasteHTML(cursorPosition, imgHtml);

      // Calculate new cursor position (after the inserted image)
      // The img tag takes 1 position in Quill's delta
      const newPosition = cursorPosition + 1;

      // Set selection after a brief delay to ensure DOM is updated
      setTimeout(() => {
        try {
          // Focus the editor first
          editor.focus();

          // Set cursor position after the inserted image
          editor.setSelection(newPosition, 0);

          // console.log("Cursor moved to position:", newPosition);
        } catch (err) {
          console.error("Could not set cursor position:", err);
          // Fallback: set cursor to end
          editor.setSelection(editor.getLength(), 0);
        }
      }, 50);

      // Update form state with the new content
      const newContent = editor.root.innerHTML;
      setFormData((prev) => ({
        ...prev,
        description: newContent,
      }));
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  };

  // Handle React Quill editor change - simplified
  const handleEditorChange = React.useCallback((value) => {
    setFormData((prev) => ({
      ...prev,
      description: value,
    }));
  }, []);

  // Handle title image upload (separate from editor images)
  const handleTitleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload an image file");
        return;
      }

      if (!file.type.startsWith("image/png")) {
        toast.error("Please upload png image file");
        return;
      }

      // console.log(file, "check file");

      // Check file size (max 5MB)
      if (file.size > 1 * 1024 * 1024) {
        toast.error("Image size should be less than 1MB");
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove title image
  const handleRemoveTitleImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Track validation errors
    const errors = [];

    // Validate Blog Title
    if (!formData.title.trim()) {
      errors.push("Blog Title is required");
    } else if (formData.title.trim().length < 3) {
      errors.push("Blog Title must be at least 3 characters");
    } else if (formData.title.trim().length > 200) {
      errors.push("Blog Title cannot exceed 200 characters");
    }

    // Validate Blog Description
    if (!formData.description.trim()) {
      errors.push("Blog Description is required");
    } else if (
      formData.description.replace(/<[^>]*>/g, "").trim().length < 10
    ) {
      errors.push(
        "Blog Description must be at least 50 characters (excluding HTML)",
      );
    }

    // Validate URL Name
    if (!formData.url_name.trim()) {
      errors.push("URL Name is required");
    } else if (!/^[a-z0-9\-]+$/.test(formData.url_name)) {
      errors.push(
        "URL Name can only contain lowercase letters, numbers, and hyphens",
      );
    } else if (formData.url_name.length < 3) {
      errors.push("URL Name must be at least 3 characters");
    } else if (formData.url_name.length > 256) {
      errors.push("URL Name cannot exceed 256 characters");
    } else if (formData.url_name.includes("--")) {
      errors.push("URL Name cannot contain consecutive hyphens");
    } else if (
      formData.url_name.startsWith("-") ||
      formData.url_name.endsWith("-")
    ) {
      errors.push("URL Name cannot start or end with a hyphen");
    }

    // Validate Meta Title
    if (!formData.meta_web_title.trim()) {
      errors.push("Meta Title is required");
    } else if (
      formData.meta_web_title.length < 3 ||
      formData.meta_web_title.length > 60
    ) {
      errors.push("Meta Title must be between 3 and 60 characters");
    }

    // Validate Meta Description
    if (!formData.meta_web_description.trim()) {
      errors.push("Meta Description is required");
    } else if (
      formData.meta_web_description.length < 3 ||
      formData.meta_web_description.length > 200
    ) {
      errors.push("Meta Description must be between 3 and 200 characters");
    }

    // Validate Open Graph Title
    if (!formData.meta_og_title.trim()) {
      errors.push("Open Graph Title is required");
    } else if (
      formData.meta_og_title.length < 3 ||
      formData.meta_og_title.length > 90
    ) {
      errors.push("Open Graph Title must be between 3 and 90 characters");
    }

    // Validate Open Graph Description
    if (!formData.meta_og_description.trim()) {
      errors.push("Open Graph Description is required");
    } else if (
      formData.meta_og_description.length < 3 ||
      formData.meta_og_description.length > 200
    ) {
      errors.push(
        "Open Graph Description must be between 3 and 200 characters",
      );
    }

    // Validate Title Image
    if (!imageFile) {
      errors.push("Title Image is required");
    } else if (!imageFile.type.startsWith("image/")) {
      errors.push("Please upload a valid image file");
    } else if (imageFile.size > 1 * 1024 * 1024) {
      errors.push("Image size should be less than 1MB");
    }

    // If there are validation errors
    if (errors.length > 0) {
      // Show first error
      toast.error(errors[0]);

      // Optionally: Show all errors in a list
      if (errors.length > 1) {
        // console.log("All validation errors:", errors);
      }

      return;
    }
    setLoading(true);

    try {
      // Create FormData for file upload
      const submitFormData = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        submitFormData.append(key, formData[key]);
      });

      // Append title image file if exists
      if (imageFile) {
        submitFormData.append("title_image", imageFile);
      }

      const response = await axios.post(
        `${app_url}/api/blogs/createBlog`,
        submitFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        toast.success("Blog created successfully!");
        navigate("/contents");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Create Blog error:", error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/contents");
  };

  if (networkIssue) {
    return <Error500 message={failed} />;
  }

  return (
    <div className="wrapper">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <p className="heading">
            <i
              className="bi bi-arrow-left-circle-fill fs-3 me-3 my-auto pointer"
              onClick={() => window.history.back()}
            ></i>
            Create New Blog
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Left Column - Main Content */}
          <div className="col-lg-7">
            {/* Basic Information Card */}
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">Basic Information</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="url_name" className="form-label">
                    Blog URL Name <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className={`form-control ${
                        formData.url_name.includes(" ") ? "is-invalid" : ""
                      }`}
                      id="url_name"
                      name="url_name"
                      value={formData.url_name}
                      onChange={(e) => {
                        // Remove spaces and convert to lowercase
                        const value = e.target.value
                          .replace(/\s+/g, "-")
                          .toLowerCase();
                        // Remove any characters that aren't URL-friendly
                        const cleanedValue = value.replace(/[^a-z0-9\-]/g, "");
                        setFormData((prev) => ({
                          ...prev,
                          url_name: cleanedValue,
                        }));
                      }}
                      placeholder="what-is-etijwaal"
                      maxLength="256"
                      required
                    />
                  </div>
                  <div className="d-flex justify-content-between mt-1">
                    <small
                      className={`${
                        formData.url_name.includes(" ")
                          ? "text-danger"
                          : "text-muted"
                      }`}
                    >
                      {formData.url_name.length} characters
                      {formData.url_name.includes(" ") &&
                        " (Spaces not allowed)"}
                    </small>
                    <small className="text-muted">
                      {256 - formData.url_name.length} remaining
                    </small>
                  </div>
                  {formData.url_name.includes(" ") && (
                    <div className="text-danger small mt-1">
                      <i className="bi bi-exclamation-circle me-1 text-danger"></i>
                      Spaces are not allowed in URLs. Use hyphens (-) instead.
                    </div>
                  )}
                  <div className="form-text">
                    SEO-friendly URL for this Blog. Use lowercase letters,
                    numbers, and hyphens only. Spaces will be automatically
                    converted to hyphens.
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="title" className="form-label">
                    Blog Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter blog title"
                    required
                    maxLength="256"
                    minLength="3"
                  />
                  <div className="form-text">
                    This will be displayed as the main question/title.
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="image" className="form-label">
                    Title Image <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    id="image"
                    accept="image/*"
                    onChange={handleTitleImageUpload}
                    required
                  />
                  <div className="form-text">
                    Upload a title image for this Blog (optional). Max size:
                    1MB.
                  </div>

                  {imagePreview && (
                    <div className="mt-3">
                      <div className="d-flex align-items-center">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="img-thumbnail me-3"
                          style={{
                            width: "150px",
                            height: "100px",
                            objectFit: "cover",
                          }}
                        />
                        <div>
                          <p className="mb-1">
                            <strong>Preview:</strong> {imageFile.name}
                          </p>
                          <p className="mb-1 text-muted">
                            Size: {(imageFile.size / 1024).toFixed(2)} KB
                          </p>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={handleRemoveTitleImage}
                          >
                            <i className="bi bi-trash me-1 "></i> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="meta_web_description" className="form-label">
                    Blog Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${
                      formData.short_detail.length > 0 &&
                      (formData.short_detail.length < 50 ||
                        formData.short_detail.length > 5000)
                        ? "is-invalid"
                        : formData.short_detail.length >= 50 &&
                            formData.short_detail.length <= 5000
                          ? "is-valid"
                          : ""
                    }`}
                    id="short_detail"
                    name="short_detail"
                    value={formData.short_detail}
                    onChange={handleInputChange}
                    rows="7"
                    placeholder="eTijwaal is a global travel eSIM service that provides..."
                    maxLength="5000"
                    required
                  />

                  <div className="d-flex justify-content-between align-items-center mt-1 gap-1">
                    <small
                      className={`${
                        formData.short_detail.length > 0 &&
                        formData.short_detail.length < 50
                          ? "text-danger"
                          : formData.short_detail.length > 5000
                            ? "text-danger"
                            : formData.short_detail.length >= 50 &&
                                formData.short_detail.length <= 5000
                              ? "text-success"
                              : "text-muted"
                      }`}
                    >
                      {formData.short_detail.length} / 5000 characters
                      {formData.short_detail.length > 0 &&
                        formData.short_detail.length < 50 &&
                        " (Min 50 characters)"}
                    </small>
                    <small className="text-muted">
                      {formData.short_detail.length >= 50 &&
                      formData.short_detail.length <= 5000
                        ? "✓ Good length"
                        : ""}
                    </small>
                  </div>

                  <small className="text-muted d-block mt-1">
                    Recommended length: Max 5000 characters results
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Blog Html Detail content</label>
                  <div className="quill-editor-grid">
                    <div className="quill-editor-main">
                      <div style={{ height: "300px", marginBottom: "50px" }}>
                        <ReactQuill
                          key="quill-editor"
                          ref={quillRef}
                          theme="snow"
                          value={formData.description}
                          onChange={handleEditorChange}
                          modules={quillModules}
                          formats={formats}
                          placeholder="Write detailed answer/description here..."
                          style={{ height: "300px" }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-text mt-4">
                    Click the image icon in the toolbar to upload images. Images
                    will be uploaded to our server.
                  </div>
                </div>

                <div className="form-check form-switch">
                  <input
                    style={{
                      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                    }}
                    className={`form-check-input active-unactive-pkg ${
                      formData.is_active === 0 ? "unactive-pkg" : "active-pkg"
                    }`}
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active === 1}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label" htmlFor="is_active">
                    Publish this Blog immediately
                  </label>
                  <div className="form-text">
                    If unchecked, the Blog will not be active and won't be
                    visible publicly.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - SEO & Actions */}
          <div className="col-lg-5">
            {/* SEO Metadata Card */}
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">SEO Metadata</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="meta_web_title" className="form-label">
                    Meta Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      formData.meta_web_title.length > 0 &&
                      (formData.meta_web_title.length < 3 ||
                        formData.meta_web_title.length > 60)
                        ? "is-invalid"
                        : formData.meta_web_title.length >= 3 &&
                            formData.meta_web_title.length <= 60
                          ? "is-valid"
                          : ""
                    }`}
                    id="meta_web_title"
                    name="meta_web_title"
                    value={formData.meta_web_title}
                    onChange={handleInputChange}
                    placeholder="What is eTijwaal | eTijwaal eSIM"
                    maxLength="60"
                    minLength="3"
                    required
                  />
                  <div className="d-flex justify-content-between mt-1 gap-1">
                    <small
                      className={`${
                        formData.meta_web_title.length > 0 &&
                        (formData.meta_web_title.length < 3 ||
                          formData.meta_web_title.length > 60)
                          ? "text-danger"
                          : formData.meta_web_title.length >= 3 &&
                              formData.meta_web_title.length <= 60
                            ? "text-success"
                            : "text-muted"
                      }`}
                    >
                      {formData.meta_web_title.length} / 60 characters
                      {formData.meta_web_title.length > 0 &&
                        formData.meta_web_title.length < 3 &&
                        " (Min 3)"}
                    </small>
                    <small className="text-muted">
                      {formData.meta_web_title.length >= 3 &&
                      formData.meta_web_title.length <= 60
                        ? `${60 - formData.meta_web_title.length} remaining`
                        : ""}
                    </small>
                  </div>
                  {formData.meta_web_title.length > 0 &&
                    formData.meta_web_title.length < 3 && (
                      <div className="text-danger small mt-1">
                        <i className="bi bi-exclamation-circle me-1 text-danger"></i>
                        Meta title is too short (minimum 3 characters)
                      </div>
                    )}
                  {formData.meta_web_title.length > 60 && (
                    <div className="text-danger small mt-1">
                      <i className="bi bi-exclamation-circle me-1 text-danger"></i>
                      Meta title is too long (maximum 60 characters)
                    </div>
                  )}
                  {formData.meta_web_title.length >= 3 &&
                    formData.meta_web_title.length <= 60 && (
                      <div className="text-success small mt-1">
                        <i className="bi bi-check-circle me-1 text-success"></i>
                        Good length for SEO
                      </div>
                    )}
                  <small className="text-muted d-block mt-1">
                    Recommended length: 50-60 characters for optimal SEO
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="meta_web_description" className="form-label">
                    Meta Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${
                      formData.meta_web_description.length > 0 &&
                      (formData.meta_web_description.length < 3 ||
                        formData.meta_web_description.length > 160)
                        ? "is-invalid"
                        : formData.meta_web_description.length >= 3 &&
                            formData.meta_web_description.length <= 160
                          ? "is-valid"
                          : ""
                    }`}
                    id="meta_web_description"
                    name="meta_web_description"
                    value={formData.meta_web_description}
                    onChange={handleInputChange}
                    rows="5"
                    placeholder="eTijwaal is a global travel eSIM service that provides..."
                    maxLength="160"
                    minLength="3"
                    required
                  />

                  <div className="d-flex justify-content-between align-items-center mt-1 gap-1">
                    <small
                      className={`${
                        formData.meta_web_description.length > 0 &&
                        formData.meta_web_description.length < 3
                          ? "text-danger"
                          : formData.meta_web_description.length > 160
                            ? "text-danger"
                            : formData.meta_web_description.length >= 3 &&
                                formData.meta_web_description.length <= 160
                              ? "text-success"
                              : "text-muted"
                      }`}
                    >
                      {formData.meta_web_description.length} / 160 characters
                      {formData.meta_web_description.length > 0 &&
                        formData.meta_web_description.length < 3 &&
                        " (Min 3)"}
                    </small>
                    <small className="text-muted">
                      {formData.meta_web_description.length >= 3 &&
                      formData.meta_web_description.length <= 160
                        ? "✓ Good length"
                        : ""}
                    </small>
                  </div>

                  {/* Validation messages */}
                  {formData.meta_web_description.length > 0 &&
                    formData.meta_web_description.length < 3 && (
                      <div className="text-danger small mt-1">
                        <i className="bi bi-exclamation-circle me-1 text-danger"></i>
                        Meta description is too short (minimum 3 characters)
                      </div>
                    )}
                  {formData.meta_web_description.length > 160 && (
                    <div className="text-danger small mt-1">
                      <i className="bi bi-exclamation-circle me-1"></i>
                      Meta description is too long (maximum 160 characters)
                    </div>
                  )}
                  {formData.meta_web_description.length >= 3 &&
                    formData.meta_web_description.length <= 160 && (
                      <div className="text-success small mt-1">
                        <i className="bi bi-check-circle me-1 text-success"></i>
                        Good length for SEO
                      </div>
                    )}
                  <small className="text-muted d-block mt-1">
                    Recommended length: 120-160 characters for optimal SEO
                    results
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="meta_og_title" className="form-label">
                    Open Graph Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      formData.meta_og_title.length > 0 &&
                      (formData.meta_og_title.length < 3 ||
                        formData.meta_og_title.length > 90)
                        ? "is-invalid"
                        : formData.meta_og_title.length >= 3 &&
                            formData.meta_og_title.length <= 90
                          ? "is-valid"
                          : ""
                    }`}
                    id="meta_og_title"
                    name="meta_og_title"
                    value={formData.meta_og_title}
                    onChange={handleInputChange}
                    placeholder="eTijwaal | Travel eSIM for What-Is-eTijwaal"
                    maxLength="90"
                    minLength="3"
                    required
                  />
                  <div className="d-flex justify-content-between mt-1 gap-1">
                    <small
                      className={`${
                        formData.meta_og_title.length > 0 &&
                        (formData.meta_og_title.length < 3 ||
                          formData.meta_og_title.length > 90)
                          ? "text-danger"
                          : formData.meta_og_title.length >= 3 &&
                              formData.meta_og_title.length <= 90
                            ? "text-success"
                            : "text-muted"
                      }`}
                    >
                      {formData.meta_og_title.length} / 90 characters
                      {formData.meta_og_title.length > 0 &&
                        formData.meta_og_title.length < 3 &&
                        " (Min 3)"}
                    </small>
                    <small className="text-muted">
                      {formData.meta_og_title.length >= 3 &&
                      formData.meta_og_title.length <= 90
                        ? `${90 - formData.meta_og_title.length} remaining`
                        : ""}
                    </small>
                  </div>
                  {formData.meta_og_title.length > 0 &&
                    formData.meta_og_title.length < 3 && (
                      <div className="text-danger small mt-1">
                        <i className="bi bi-exclamation-circle me-1 text-danger"></i>
                        Open Graph title is too short (minimum 3 characters)
                      </div>
                    )}
                  {formData.meta_og_title.length > 90 && (
                    <div className="text-danger small mt-1">
                      <i className="bi bi-exclamation-circle me-1 text-danger"></i>
                      Open Graph title is too long (maximum 90 characters)
                    </div>
                  )}
                  {formData.meta_og_title.length >= 3 &&
                    formData.meta_og_title.length <= 90 && (
                      <div className="text-success small mt-1">
                        <i className="bi bi-check-circle me-1 text-success"></i>
                        Good length for social sharing
                      </div>
                    )}
                  <small className="text-muted d-block mt-1">
                    Recommended for optimal social media display
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="meta_og_description" className="form-label">
                    Open Graph Description{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${
                      formData.meta_og_description.length > 0 &&
                      (formData.meta_og_description.length < 3 ||
                        formData.meta_og_description.length > 200)
                        ? "is-invalid"
                        : formData.meta_og_description.length >= 3 &&
                            formData.meta_og_description.length <= 200
                          ? "is-valid"
                          : ""
                    }`}
                    id="meta_og_description"
                    name="meta_og_description"
                    value={formData.meta_og_description}
                    onChange={handleInputChange}
                    rows="5"
                    placeholder="Buy a travel eSIM for What Is eTijwaal with instant activation..."
                    maxLength="200"
                    required
                  />
                  <div className="d-flex justify-content-between align-items-center mt-1 gap-1">
                    <small
                      className={`${
                        formData.meta_og_description.length > 0 &&
                        formData.meta_og_description.length < 3
                          ? "text-danger"
                          : formData.meta_og_description.length > 200
                            ? "text-danger"
                            : formData.meta_og_description.length >= 3 &&
                                formData.meta_og_description.length <= 200
                              ? "text-success"
                              : "text-muted"
                      }`}
                    >
                      {formData.meta_og_description.length} / 200 characters
                      {formData.meta_og_description.length > 0 &&
                        formData.meta_og_description.length < 3 &&
                        " (Min 3)"}
                    </small>
                    <small className="text-muted">
                      {formData.meta_og_description.length >= 3 &&
                      formData.meta_og_description.length <= 200
                        ? "✓ Good length"
                        : ""}
                    </small>
                  </div>
                  {formData.meta_og_description.length > 0 &&
                    formData.meta_og_description.length < 3 && (
                      <div className="text-danger small mt-1">
                        <i className="bi bi-exclamation-circle me-1 text-danger"></i>
                        Open Graph description is too short (minimum 3
                        characters)
                      </div>
                    )}
                  {formData.meta_og_description.length > 200 && (
                    <div className="text-danger small mt-1">
                      <i className="bi bi-exclamation-circle me-1 text-danger"></i>
                      Open Graph description is too long (maximum 200
                      characters)
                    </div>
                  )}
                  {formData.meta_og_description.length >= 3 &&
                    formData.meta_og_description.length <= 200 && (
                      <div className="text-success small mt-1">
                        <i className="bi bi-check-circle me-1 text-success"></i>
                        Good length for social sharing
                      </div>
                    )}
                  <small className="text-muted d-block mt-1">
                    Recommended length: 120-160 characters for optimal social
                    media display
                  </small>
                </div>
              </div>
            </div>

            {/* Quick Actions Card */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="d-grid gap-2">
                  <button
                    className="btn primary-btn w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2 text-white"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Creating Blog...
                      </>
                    ) : (
                      <>Create Blog</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onUpload={handleImageUploadFromModal}
      />

      {/* Sticky Save Bar for Mobile */}
      <div className="d-lg-none fixed-bottom bg-white border-top p-3 shadow">
        <div className="d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Blog"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateBlog;
